import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFormProgress } from '../../../hooks/useFormProgress';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { fetchApplicationDetails, uploadFormFile, saveFormData } from '../../../utils/apiUtils';
import styled, { createGlobalStyle } from 'styled-components';
import FilePreviewModal from '../../common/FilePreviewModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';
import ApplicationIdDisplay from '../../common/ApplicationIdDisplay';

// API URL configuration
const API_URL_CONFIG = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL_CONFIG.endsWith('/api') ? API_URL_CONFIG.slice(0, -4) : API_URL_CONFIG;

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
// 210mm / 297mm = 0.707
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Global style to inject shake keyframes into global CSS for inline style usage
const GlobalShakeStyle = createGlobalStyle`
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
    20%, 40%, 60%, 80% { transform: translateX(8px); }
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545; /* Red for offline */
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000; /* Ensure it's above other elements */
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px; /* Use min-height to allow content to expand if needed */
  background-color: white;
  padding: 50px; /* Increased padding for more breathing room */
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07); /* Softer, more modern shadow */
  box-sizing: border-box;
  position: relative; /* For page number positioning */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  color: #212529; /* Darker, more standard text color */
  font-size: 13px; /* Base font size for content */
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2); /* Modern gradient */
  color: white;
  border: none;
  border-radius: 50%; /* Circular button */
  width: 60px; /* Size of the button */
  height: 60px;
  font-size: 22px; /* Slightly smaller icon size for a cleaner look */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000; /* Ensure it's on top */
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &::after {
    content: 'Back';
    position: absolute;
    font-size: 12px;
    bottom: 7px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const FormContentContainer = styled.div`
  /* Form content container */
`;

const FormFieldFullWidth = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const Label = styled.label`
  margin-right: 8px;
  font-weight: 500;
  color: #495057;
  font-size: 0.9em;
  margin-bottom: 4px;
  /* Ensure proper wrapping for long labels */
  display: block; /* Labels are inline by default, block helps with width */
  width: 100%; /* Take full available width */
  white-space: normal; /* Default, but explicit to override potential inheritance */
  overflow-wrap: break-word; /* Standard property to break long words/strings */
`;

const StyledInput = styled.input`
  flex-grow: 1;
  border: 1px solid #a8d0b9;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 1em;
  font-family: inherit;
  background-color: #f8fcfa;
  color: #495057;
  line-height: 1.5;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #6cb288;
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border: 1px solid #a8d0b9;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 1em;
  font-family: inherit;
  background-color: #f8fcfa;
  color: #495057;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #6cb288;
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 5px;
  margin-bottom: 10px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 6px;
  
  input[type="radio"] {
    appearance: none;
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid #6cb288;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    
    &:checked {
      border-color: #6cb288;
      background-color: #f0f9f4;
      
      &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: 3px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #6cb288;
      }
    }
  }
  
  &:hover input[type="radio"] {
    border-color: #4d9e70;
    box-shadow: 0 0 0 1px rgba(108, 178, 136, 0.2);
  }
`;

const InlineGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  
  > div {
    flex: 1;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.1em;
  font-weight: 600;
  color: #0056b3;
  margin-top: 25px;
  margin-bottom: 15px;
  padding-bottom: 5px;
  border-bottom: 1px solid #e0e0e0;
`;

const InfoText = styled.p`
  font-size: 0.85em;
  color: #666;
  margin-bottom: 15px;
  line-height: 1.5;
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const SaveDraftButton = styled.button`
  background-color: #ffc107;
  color: #212529;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px;
  margin-right: 10px;
  display: inline-block;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const FileUploadButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 6px 14px;
  border: none;
  border-radius: 4px;
  font-size: 0.85em;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ViewButton = styled.button`
  background-color: #28a745;
  color: white;
  padding: 6px 14px;
  border: none;
  border-radius: 4px;
  font-size: 0.85em;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-left: 6px;

  &:hover {
    background-color: #218838;
  }
`;

const UploadSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s ease-in-out infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const UploadErrorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const UploadErrorContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const DocumentPreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const DocumentPreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 900px;
  padding: 15px 20px;
  background: #1a5276;
  color: white;
  border-radius: 8px 8px 0 0;
`;

const DocumentPreviewContent = styled.div<{ scale: number }>`
  background: white;
  width: 100%;
  max-width: 900px;
  height: 70vh;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 0 8px 8px;
  
  iframe, img {
    transform: scale(${props => props.scale});
    transform-origin: center center;
    transition: transform 0.2s ease;
  }
`;

const PreviewZoomControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const PreviewZoomButton = styled.button`
  background: white;
  color: #1a5276;
  border: none;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #e8f4fc;
  }
`;

const ClosePreviewButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #c82333;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px;
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

interface FormData {
  applicationNoOfficeUse: string;
  dealershipType?: string; // Application type from Page 1
  // Page 1 fields needed for green tick indicators
  panCardFile?: File | string | null;
  aadhaarFile?: File | string | null;
  addressProofFile?: File | string | null;
  currentAddress?: string;
  qualificationProofFile?: File | string | null;
  eduSscCertificateFile?: File | string | null;
  eduHscCertificateFile?: File | string | null;
  eduGraduateCertificateFile?: File | string | null;
  eduOthersCertificateFile?: File | string | null;
  // Document Checklist Files (20 documents)
  doc1File?: File | string | null; // Certified copy of Ownership / Title Document
  doc2File?: File | string | null; // Notarized Affidavit of Title of Ownership
  doc3File?: File | string | null; // Property Card or 7/12 Extract
  doc4File?: File | string | null; // Ferfar / Mutation Record / Entry
  doc5File?: File | string | null; // Previous 5 years Property Tax / Dues payment receipts
  doc6aFile?: File | string | null; // Registered Partnership Deed (Partnership Firm)
  doc6bFile?: File | string | null; // Proposed Partnership Deed (Partnership Firm)
  doc7File?: File | string | null; // Certificate of Registration for Co-operatives / Corporate Bodies
  doc8File?: File | string | null; // Document indicating Commercial Status
  doc9File?: File | string | null; // Land Lease Agreement between MIDC & Applicant
  doc10File?: File | string | null; // Survey Plan issued by MIDC
  doc11File?: File | string | null; // NOC from Landlord (Leasehold Land)
  doc12File?: File | string | null; // NOC from MHADA / MIDC / Municipal Corporation
  doc13File?: File | string | null; // NA Order
  doc14File?: File | string | null; // Partition / Distribution Deed
  doc15File?: File | string | null; // Firm Allotment Letter
  doc16File?: File | string | null; // Notarized Affidavit from all Owners (POA)
  doc17File?: File | string | null; // Proof of Date of Birth
  doc18File?: File | string | null; // Proof of Residence
  doc19File?: File | string | null; // Proof of Educational Qualification
  doc20File?: File | string | null; // Proof of Photo ID & Address (Aadhaar)
  errors?: {
    // Add error fields as needed
  };
}

interface ApplicationFormPage4Props {
  formData?: any;
  isViewMode?: boolean;
}

const initialState: FormData = {
  applicationNoOfficeUse: '',
  // Document Checklist Files
  doc1File: null,
  doc2File: null,
  doc3File: null,
  doc4File: null,
  doc5File: null,
  doc6aFile: null,
  doc6bFile: null,
  doc7File: null,
  doc8File: null,
  doc9File: null,
  doc10File: null,
  doc11File: null,
  doc12File: null,
  doc13File: null,
  doc14File: null,
  doc15File: null,
  doc16File: null,
  doc17File: null,
  doc18File: null,
  doc19File: null,
  doc20File: null,
  errors: {}
};

const ApplicationFormPage4: React.FC<ApplicationFormPage4Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Application lock status (locked after payment or manually locked by admin) - declare early
  const [isApplicationLocked, setIsApplicationLocked] = useState<boolean>(false);

  // Read editingUser from sessionStorage (set by Page1's partner selection modal)
  const [editingUser] = useState<'main' | number | null>(() => {
    const stored = sessionStorage.getItem('editingUser');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });
  
  // Check if main applicant section should be locked (when a partner is editing)
  // Document Checklist is a main applicant responsibility - partners can only view
  const isMainApplicantSectionLocked = editingUser !== null && editingUser !== 'main';
  
  // Combined disabled state for fields
  const isFieldDisabled = isMainApplicantSectionLocked || isApplicationLocked;

  const [applicationId, setApplicationId] = useState<string>('');
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(propFormData || { ...initialState });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [showBrochureModal, setShowBrochureModal] = useState<boolean>(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  const [previewZoom, setPreviewZoom] = useState<number>(1.0);
  
  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Mandatory field validation state and refs for doc2 and doc3
  const doc2RowRef = useRef<HTMLTableRowElement>(null);
  const doc3RowRef = useRef<HTMLTableRowElement>(null);
  const [isDoc2Shaking, setIsDoc2Shaking] = useState<boolean>(false);
  const [isDoc3Shaking, setIsDoc3Shaking] = useState<boolean>(false);

  // PDF generation state
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  // Row visibility state based on application type - initialize with all rows as default
  const [visibleRows, setVisibleRows] = useState<string[]>(['doc1', 'doc2', 'doc3', 'doc4', 'doc5', 'doc6a', 'doc6b', 'doc7', 'doc8', 'doc9', 'doc10', 'doc11', 'doc12', 'doc13', 'doc14', 'doc15', 'doc16', 'doc17', 'doc18', 'doc19', 'doc20']);
  const [visibilityLoaded, setVisibilityLoaded] = useState(false);

  // Upload states for document fields
  type DocField = 'doc1File' | 'doc2File' | 'doc3File' | 'doc4File' | 'doc5File' | 'doc6aFile' | 'doc6bFile' | 'doc7File' | 'doc8File' | 'doc9File' | 'doc10File' | 'doc11File' | 'doc12File' | 'doc13File' | 'doc14File' | 'doc15File' | 'doc16File' | 'doc17File' | 'doc18File' | 'doc19File' | 'doc20File';
  const [uploadStatus, setUploadStatus] = useState<Partial<Record<DocField, 'idle' | 'uploading' | 'success' | 'error'>>>({});
  const [uploadProgress, setUploadProgress] = useState<Partial<Record<DocField, number>>>({});
  const [uploadErrorModal, setUploadErrorModal] = useState<{ show: boolean; message: string; fieldName: string }>({ show: false, message: '', fieldName: '' });

  // Document field name to display name mapping
  const DOC_DISPLAY_NAMES: Record<DocField, string> = {
    'doc1File': 'Ownership_Title_Document',
    'doc2File': 'Notarized_Affidavit_Title',
    'doc3File': 'Property_Card_7_12_Extract',
    'doc4File': 'Ferfar_Mutation_Record',
    'doc5File': 'Property_Tax_Receipts',
    'doc6aFile': 'Partnership_Deed_Registration',
    'doc6bFile': 'Partnership_Deed_Notarized',
    'doc7File': 'Certificate_Registration_Corporate',
    'doc8File': 'Commercial_Status_Document',
    'doc9File': 'Land_Lease_Agreement_MIDC',
    'doc10File': 'Survey_Plan_Handover_Letter',
    'doc11File': 'NOC_Landlord',
    'doc12File': 'NOC_MHADA_MIDC',
    'doc13File': 'NA_Order',
    'doc14File': 'Partition_Distribution_Deed',
    'doc15File': 'Allotment_Letter_Govt',
    'doc16File': 'POA_Affidavit',
    'doc17File': 'DOB_Proof',
    'doc18File': 'Residence_Proof',
    'doc19File': 'Educational_Qualification_Proof',
    'doc20File': 'Photo_ID_Address_Proof_Aadhaar',
  };

  // Global progress bar (across Page1+2+3+4) — PageA and Page5 are info-only
  const formProgress = useFormProgress(formData);

  // Effect to fetch row visibility based on dealershipType
  useEffect(() => {
    const fetchRowVisibility = async () => {
      // Get dealershipType from formData (loaded from backend)
      const dealershipType = formData.dealershipType || propFormData?.dealershipType || 'Individual';
      
      try {
        const response = await fetch(`${API_BASE}/api/page4-row-visibility/${encodeURIComponent(dealershipType)}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.visibleRows) {
            setVisibleRows(data.visibleRows);
          } else {
            // Default: show all rows
            setVisibleRows(['doc1', 'doc2', 'doc3', 'doc4', 'doc5', 'doc6a', 'doc6b', 'doc7', 'doc8', 'doc9', 'doc10', 'doc11', 'doc12', 'doc13', 'doc14', 'doc15', 'doc16', 'doc17', 'doc18', 'doc19', 'doc20']);
          }
        } else {
          // Default: show all rows on error
          setVisibleRows(['doc1', 'doc2', 'doc3', 'doc4', 'doc5', 'doc6a', 'doc6b', 'doc7', 'doc8', 'doc9', 'doc10', 'doc11', 'doc12', 'doc13', 'doc14', 'doc15', 'doc16', 'doc17', 'doc18', 'doc19', 'doc20']);
        }
      } catch (error) {
        console.error('Error fetching page4 row visibility:', error);
        // Default: show all rows on error
        setVisibleRows(['doc1', 'doc2', 'doc3', 'doc4', 'doc5', 'doc6a', 'doc6b', 'doc7', 'doc8', 'doc9', 'doc10', 'doc11', 'doc12', 'doc13', 'doc14', 'doc15', 'doc16', 'doc17', 'doc18', 'doc19', 'doc20']);
      } finally {
        setVisibilityLoaded(true);
      }
    };

    fetchRowVisibility();
  }, [formData.dealershipType, propFormData?.dealershipType, API_BASE]);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    // If formData is passed as a prop, its ID should be authoritative.
    if (propFormData?.applicationNoOfficeUse) {
      setApplicationId(propFormData.applicationNoOfficeUse);
      return;
    }

    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[ApplicationFormPage4 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[ApplicationFormPage4 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true;
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  }, [location.state, propFormData]);

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    if (propFormData) return; // Skip loading if formData is passed as a prop

    const loadDraftData = async () => {
      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[ApplicationFormPage4 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          const appData = await fetchApplicationDetails(applicationId);
          if (appData && appData.formData) {
            const loadedFormData = {
              ...appData.formData,
            };
            setFormData(loadedFormData);
            console.log(`[ApplicationFormPage4 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[ApplicationFormPage4 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[ApplicationFormPage4 LoadDraft] Error loading draft data for ${applicationId}:`, error);
        }
      }
    };

    loadDraftData();
  }, [applicationId, isViewMode, propFormData]);

  // Effect to check if application is locked (after payment or manually locked by admin)
  useEffect(() => {
    const checkLockStatus = async () => {
      if (!applicationId) return;
      try {
        const response = await axios.get(`${API_BASE}/api/settings/application-lock-status/${applicationId}`);
        if (response.data.success && response.data.isLocked) {
          setIsApplicationLocked(true);
          console.log(`[AppFormPage4] Application ${applicationId} is locked: ${response.data.reason}`);
        } else {
          setIsApplicationLocked(false);
        }
      } catch (error) {
        console.error('[AppFormPage4] Error checking application lock status:', error);
      }
    };
    checkLockStatus();
  }, [applicationId]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleOpenFilePreview = (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    console.log('[handleOpenFilePreview] Current API_BASE_URL:', API_BASE_URL);

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      console.log('[handleOpenFilePreview] File is a string:', file);

      // Determine backend origin consistently using REACT_APP_API_URL if absolute; otherwise fallback to current origin
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const urlObject = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${urlObject.protocol}//${urlObject.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      }

      // Normalize to a path
      let normalizedPath = file;
      if (file.includes('://')) {
        try { normalizedPath = new URL(file).pathname; } catch { normalizedPath = file; }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.startsWith('uploads/') ? `/${normalizedPath}` : `/uploads/${normalizedPath}`;
      }

      url = `${serverBaseForFiles}${normalizedPath}`;
      name = normalizedPath.split('/').pop() || defaultFileName;

      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (lower.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = lower.split('.').pop();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream';
      }
      console.log(`[handleOpenFilePreview] Constructed URL: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      console.log('[handleOpenFilePreview] File is a File object:', file);
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] File object - Constructed URL:', url, 'Name:', name, 'Type:', type);
    }

    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    const formElement = formRef.current;
    if (!formElement) {
      alert("Error: Form element not found.");
      console.error("PDF generation failed: formRef.current is null.");
      return;
    }

    const formClone = formElement.cloneNode(true) as HTMLElement;

    try {
      formClone.style.position = 'absolute';
      formClone.style.left = '-9999px';
      formClone.style.top = '0px';
      formClone.style.width = `${formElement.offsetWidth}px`;
      document.body.appendChild(formClone);

      const inputs = formClone.querySelectorAll('input[type="text"], input[type="number"], textarea');
      inputs.forEach(input => {
        const typedInput = input as HTMLInputElement | HTMLTextAreaElement;
        const value = typedInput.value;
        if (value) {
          const span = document.createElement('span');
          span.textContent = value;
          span.style.borderBottom = '1px solid #333';
          span.style.padding = '2px 5px';
          span.style.margin = '0 5px';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.whiteSpace = 'pre-wrap';
          span.style.wordBreak = 'break-word';
          span.style.color = window.getComputedStyle(typedInput).color;
          typedInput.parentNode?.replaceChild(span, typedInput);
        }
      });

      const checkboxes = formClone.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      checkboxes.forEach(checkbox => {
        const typedCheckbox = checkbox as HTMLInputElement;
        const span = document.createElement('span');
        span.textContent = typedCheckbox.checked ? '☑' : '☐';
        span.style.fontSize = 'inherit';
        span.style.fontFamily = 'inherit';
        span.style.display = 'inline-block';
        span.style.margin = '0 5px';
        typedCheckbox.parentNode?.replaceChild(span, typedCheckbox);
      });

      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => (btn.style.visibility = 'hidden'));

      const canvas = await html2canvas(formClone, {
        scale: 2,
        useCORS: true,
        width: formClone.scrollWidth,
        height: formClone.scrollHeight,
        windowWidth: formClone.scrollWidth,
        windowHeight: formClone.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.5); // Use aggressive JPEG compression
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      
      // Simulate compression process for visual feedback
      for (let i = 0; i <= 100; i += 10) {
        setCompressionProgress(i);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      pdf.save(`application_form_page4_${applicationId || 'draft'}.pdf`);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert(`Error generating PDF for Page 4. Please check the console for details. Error: ${error instanceof Error ? error.message : String(error)}`);
      // Ensure buttons are visible even if an error occurs
      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => btn.style.visibility = 'visible');
    } finally {
      if (formClone.parentNode === document.body) {
        document.body.removeChild(formClone);
      }
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  // Function to save data to database
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      return;
    }

    if (!isOnline) {
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.');
      return;
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      const dataToSave = {
        ...data,
        // Handle file fields: if it's a File object, take its name; if it's already a string, keep it
        doc1File: data.doc1File instanceof File ? data.doc1File.name : data.doc1File,
        doc2File: data.doc2File instanceof File ? data.doc2File.name : data.doc2File,
        doc3File: data.doc3File instanceof File ? data.doc3File.name : data.doc3File,
        doc4File: data.doc4File instanceof File ? data.doc4File.name : data.doc4File,
        doc5File: data.doc5File instanceof File ? data.doc5File.name : data.doc5File,
        doc6aFile: data.doc6aFile instanceof File ? data.doc6aFile.name : data.doc6aFile,
        doc6bFile: data.doc6bFile instanceof File ? data.doc6bFile.name : data.doc6bFile,
        doc7File: data.doc7File instanceof File ? data.doc7File.name : data.doc7File,
        doc8File: data.doc8File instanceof File ? data.doc8File.name : data.doc8File,
        doc9File: data.doc9File instanceof File ? data.doc9File.name : data.doc9File,
        doc10File: data.doc10File instanceof File ? data.doc10File.name : data.doc10File,
        doc11File: data.doc11File instanceof File ? data.doc11File.name : data.doc11File,
        doc12File: data.doc12File instanceof File ? data.doc12File.name : data.doc12File,
        doc13File: data.doc13File instanceof File ? data.doc13File.name : data.doc13File,
        doc14File: data.doc14File instanceof File ? data.doc14File.name : data.doc14File,
        doc15File: data.doc15File instanceof File ? data.doc15File.name : data.doc15File,
        doc16File: data.doc16File instanceof File ? data.doc16File.name : data.doc16File,
        doc17File: data.doc17File instanceof File ? data.doc17File.name : data.doc17File,
        doc18File: data.doc18File instanceof File ? data.doc18File.name : data.doc18File,
        doc19File: data.doc19File instanceof File ? data.doc19File.name : data.doc19File,
        doc20File: data.doc20File instanceof File ? data.doc20File.name : data.doc20File,
      };
      
      const response = await saveFormData(idToUse, dataToSave, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false;
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline]);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[ApplicationFormPage4 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId);
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline]);

  const handleFillDummyData = () => {
    console.log('[ApplicationFormPage4 FillDummyData] Document checklist - no dummy data for file uploads.');
    setSaveMessage('Document uploads cannot be auto-filled.');
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
    }, 3000);
  };

  // Handle file upload for a specific document with animation and AI validation
  const handleFileUpload = (fieldName: keyof FormData) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const docFieldName = fieldName as DocField;
    
    if (e.target.files?.[0] && applicationId) {
      const file = e.target.files[0];
      
      // Global file type validation - block unsupported file types
      const blockedExtensions = ['.doc', '.docx', '.xlsx', '.xls', '.txt', '.bmp'];
      const blockedMimeTypes = [
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'image/bmp'
      ];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (blockedExtensions.includes(fileExtension) || blockedMimeTypes.includes(file.type)) {
        setUploadErrorModal({
          show: true,
          message: `File type "${fileExtension}" is not supported. Please upload only JPG, JPEG, PNG, or PDF files.`,
          fieldName: DOC_DISPLAY_NAMES[docFieldName] || fieldName
        });
        return;
      }
      
      // Set uploading status with animation
      setUploadStatus(prev => ({ ...prev, [docFieldName]: 'uploading' }));
      setUploadProgress(prev => ({ ...prev, [docFieldName]: 0 }));
      setShowSaveIndicator(true);
      setSaveMessage('Uploading file...');
      
      try {
        // Upload file to server with progress tracking
        const response = await uploadFormFile(
          applicationId,
          file,
          fieldName,
          (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({ ...prev, [docFieldName]: percent }));
              setSaveMessage(`Uploading... ${percent}%`);
            }
          }
        );
        
        if (response.success && response.filePath) {
          // Update formData with the server file path
          const updatedFormData = { ...formData, [fieldName]: response.filePath };
          setFormData(updatedFormData);
          setUploadStatus(prev => ({ ...prev, [docFieldName]: 'success' }));
          setSaveMessage('File uploaded successfully');
          
          // Auto-save the form data after successful upload
          saveToDatabaseCallback(updatedFormData, true);
        } else {
          setUploadStatus(prev => ({ ...prev, [docFieldName]: 'error' }));
          setSaveMessage('Error uploading file');
          setTimeout(() => setShowSaveIndicator(false), 3000);
        }
      } catch (error: any) {
        console.error('Error uploading file:', error);
        setUploadStatus(prev => ({ ...prev, [docFieldName]: 'error' }));
        setUploadProgress(prev => ({ ...prev, [docFieldName]: 0 }));
        
        // Extract error message for AI validation failures
        const errorMessage = error.message || 'File upload failed. Please try again.';
        
        // Show error modal for better user feedback
        setUploadErrorModal({ 
          show: true, 
          message: errorMessage, 
          fieldName: DOC_DISPLAY_NAMES[docFieldName] || fieldName 
        });
        
        setSaveMessage('Upload failed');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      } finally {
        // Clear upload progress after a delay
        setTimeout(() => {
          setUploadProgress(prev => ({ ...prev, [docFieldName]: 0 }));
        }, 500);
      }
    } else if (!applicationId) {
      setSaveMessage('Error: Application ID missing');
      setShowSaveIndicator(true);
      setTimeout(() => setShowSaveIndicator(false), 3000);
    }
    
    // Reset the file input so the same file can be re-selected if needed
    e.target.value = '';
  };

  // Handle viewing uploaded file
  const handleViewFile = (file: File | string | null | undefined, fileName: string) => {
    if (!file) return;
    
    let fileUrl: string;
    let fileType: string;
    
    if (file instanceof File) {
      fileUrl = URL.createObjectURL(file);
      fileType = file.type;
      console.log('[handleViewFile] File object - URL:', fileUrl, 'Type:', fileType);
    } else {
      // It's a string (file path from backend)
      // In production: files are served from the same origin via reverse proxy
      // In development: files are on backend port 5000
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const urlObject = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${urlObject.protocol}//${urlObject.host}`;
        } catch (e) {
          console.warn('[handleViewFile] Could not parse REACT_APP_API_URL:', e);
          serverBaseForFiles = window.location.origin;
        }
      } else {
        // Relative API URL - check if we're in development
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDevelopment && window.location.port === '3000') {
          serverBaseForFiles = `${window.location.protocol}//${window.location.hostname}:5000`;
        }
      }
      
      // Normalize the path
      let normalizedPath = file;
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.startsWith('uploads/') ? `/${normalizedPath}` : `/uploads/${normalizedPath}`;
      }
      
      fileUrl = `${serverBaseForFiles}${normalizedPath}`;
      fileType = file.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/*';
      console.log('[handleViewFile] String path - URL:', fileUrl, 'Type:', fileType);
    }
    
    setModalFileUrl(fileUrl);
    setModalFileName(fileName);
    setModalFileType(fileType);
    setIsModalOpen(true);
  };

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      if (applicationId) {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}`);
      }
    }, 1000);
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };

  // Validate mandatory documents before navigation
  const validateMandatoryFields = (): boolean => {
    // Check doc2 (Notarized Affidavit) - only if visible
    if (visibleRows.includes('doc2') && !formData.doc2File) {
      setIsDoc2Shaking(true);
      setTimeout(() => setIsDoc2Shaking(false), 500);
      doc2RowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    
    // Check doc3 (Property Card) - only if visible
    if (visibleRows.includes('doc3') && !formData.doc3File) {
      setIsDoc3Shaking(true);
      setTimeout(() => setIsDoc3Shaking(false), 500);
      doc3RowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    
    return true;
  };

  const handleNextButtonClick = () => {
    // Validate mandatory fields first
    if (!validateMandatoryFields()) {
      return;
    }
    
    // Show save modal before navigation (skip if application is locked)
    if (applicationId && isOnline && !isApplicationLocked) {
      setPendingNavigation('/form/page14');
      setShowSaveModal(true);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate('/form/page14', { state: { applicationId } });
    }
  };

  // Handle save modal actions
  const handleSaveAndNavigate = async () => {
    setShowSaveModal(false);
    
    if (applicationId && isOnline) {
      console.log('[Navigation Page4] Saving draft before navigating');
      await saveToDatabaseCallback(formData, true, applicationId);
      
      // Wait 1.5 seconds so user can see the "Draft saved successfully" message
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleSkipSaveAndNavigate = () => {
    setShowSaveModal(false);
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  // Handle navigation from vertical nav bar - show save modal
  const handleNavBarNavigation = (targetPath: string) => {
    // Only validate when navigating forward (to page 5 or beyond)
    if (targetPath.includes('page5') || targetPath.includes('page14') || 
        parseInt(targetPath.replace(/\D/g, '')) > 4) {
      if (!validateMandatoryFields()) {
        return;
      }
    }
    
    // Skip save modal if application is locked
    if (applicationId && isOnline && !isApplicationLocked) {
      setPendingNavigation(targetPath);
      setShowSaveModal(true);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(targetPath, { state: { applicationId } });
    }
  };

  // Form progress is now calculated by the shared useFormProgress hook (global across Page1+2+3+4)

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      {/* Global shake animation keyframes */}
      <GlobalShakeStyle />
      
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel>Page 4/5 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      {/* Application Locked Banner */}
      {isApplicationLocked && !isViewMode && (
        <div style={{
          position: 'fixed',
          top: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(254, 243, 199, 0.92)',
          backdropFilter: 'blur(8px)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '10px 20px',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)',
          maxWidth: '90%'
        }}>
          <span style={{ fontSize: '1.3rem' }}>🔒</span>
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.9rem' }}>
              Application Locked
            </div>
            <div style={{ color: '#78350f', fontSize: '0.75rem' }}>
              This application is locked. You can view data but cannot make changes.
            </div>
          </div>
        </div>
      )}
      
      <FloatingBackButton onClick={() => { window.scrollTo({ top: 0, behavior: 'auto' }); navigate('/form/pageA', { state: { applicationId } }); }} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      <div style={{ position: 'fixed', top: '80px', left: '30px', zIndex: 1001 }}>
        <button 
          onClick={() => setShowBrochureModal(true)}
          style={{ 
            backgroundColor: '#2196f3', 
            color: '#ffffff', 
            borderRadius: '4px', 
            padding: '8px 15px',
            border: '1px solid #1976d2',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          📄 View Brochure
        </button>
      </div>
      
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      
      <FormPageNavigator currentPage={4} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} onNavigate={handleNavBarNavigation} />
      
      <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page →
        </NextPageButton>
      </div>
      {/* WhatsApp Help Button */}
      <div style={{ position: 'fixed', right: '20px', bottom: '100px', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#25D366', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>Need Help!</span>
        <a 
          href="https://web.whatsapp.com/send?phone=919971874909&text=Hi%2C%20Need%20Help%20filling%20MNGL%20DODO%20form"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', cursor: 'pointer', transition: 'transform 0.2s ease' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title="Chat with us on WhatsApp"
        >
          <img src="/assets/images/whatsapp.png" alt="WhatsApp Help" style={{ }} />
        </a>
      </div>
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            {/* Document Checklist Section */}
            <FormFieldFullWidth style={{ marginTop: '10px' }}>
              <Label style={{ fontWeight: 700, marginBottom: '20px', color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '1.1em', textDecoration: 'underline' }}>
                DOCUMENT CHECKLIST
                {isMainApplicantSectionLocked && <span style={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '10px', textDecoration: 'none' }}>🔒 Disabled</span>}
              </Label>
              <div style={{ fontSize: '0.95em', textAlign: 'center', marginBottom: '20px', color: '#666', fontStyle: 'italic' }}>
                List of Documents to be Uploaded (Scanned Copy in PDF Format)
              </div>
              
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#1a5276', color: 'white', fontWeight: 600, width: '50px', textAlign: 'center' }}>Sr. No.</th>
                    <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#1a5276', color: 'white', fontWeight: 600 }}>Documents Required</th>
                    <th style={{ padding: '10px', border: '1px solid #85c1e9', backgroundColor: '#1a5276', color: 'white', fontWeight: 600, width: '180px', textAlign: 'center' }}>Upload</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.includes('doc1') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>1</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Certified copy of Ownership / Title Document</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc1File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc1File'] === 'uploading'} style={{ opacity: uploadStatus['doc1File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc1File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc1File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc1File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc1File'] || 0}%</span>}
                        {formData.doc1File && <ViewButton onClick={() => handleViewFile(formData.doc1File, 'Ownership / Title Document')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc1File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc1File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc1File && uploadStatus['doc1File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc2') && (
                  <tr ref={doc2RowRef}>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>2</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        Notarized Affidavit of Title of Ownership (Appendix D) on Rs. 500 stamp paper
                        {!formData.doc2File && (
                          <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', animation: isDoc2Shaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc2File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc2File'] === 'uploading'} style={{ opacity: uploadStatus['doc2File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc2File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc2File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc2File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc2File'] || 0}%</span>}
                        {formData.doc2File && <ViewButton onClick={() => handleViewFile(formData.doc2File, 'Notarized Affidavit of Title')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc2File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc2File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc2File && uploadStatus['doc2File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc3') && (
                  <tr ref={doc3RowRef}>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>3</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <span>Property Card or 7/12 Extract of recent date (Original) – <em style={{ color: '#666' }}>The attached Property Card / 7/12 extract must be issued within one month from the date of application</em></span>
                        {!formData.doc3File && (
                          <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600, whiteSpace: 'nowrap' }}>Required</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', animation: isDoc3Shaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc3File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc3File'] === 'uploading'} style={{ opacity: uploadStatus['doc3File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc3File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc3File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc3File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc3File'] || 0}%</span>}
                        {formData.doc3File && <ViewButton onClick={() => handleViewFile(formData.doc3File, 'Property Card / 7/12 Extract')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc3File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc3File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc3File && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc4') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>4</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Ferfar / Mutation Record / Entry (Original)</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc4File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc4File'] === 'uploading'} style={{ opacity: uploadStatus['doc4File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc4File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc4File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc4File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc4File'] || 0}%</span>}
                        {formData.doc4File && <ViewButton onClick={() => handleViewFile(formData.doc4File, 'Ferfar / Mutation Record')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc4File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc4File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc4File && uploadStatus['doc4File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc5') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>5</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Previous 5 years' Property Tax / Dues payment receipts along with latest Tax NOC (Original)</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc5File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc5File'] === 'uploading'} style={{ opacity: uploadStatus['doc5File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc5File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc5File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc5File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc5File'] || 0}%</span>}
                        {formData.doc5File && <ViewButton onClick={() => handleViewFile(formData.doc5File, 'Property Tax Receipts')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc5File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc5File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc5File && uploadStatus['doc5File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc6a') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }} rowSpan={visibleRows.includes('doc6b') ? 2 : 1}>6</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}><strong>Partnership Firm (if applicable):</strong><br/>a) Registered Partnership Deed along with Certificate of Registration (Certified Copy)</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc6aFile')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc6aFile'] === 'uploading'} style={{ opacity: uploadStatus['doc6aFile'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc6aFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc6aFile ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc6aFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc6aFile'] || 0}%</span>}
                        {formData.doc6aFile && <ViewButton onClick={() => handleViewFile(formData.doc6aFile, 'Registered Partnership Deed')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc6aFile" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc6aFile')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc6aFile && uploadStatus['doc6aFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc6b') && (
                  <tr>
                    {!visibleRows.includes('doc6a') && <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>6</td>}
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>b) Proposed Partnership Deed duly notarized</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc6bFile')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc6bFile'] === 'uploading'} style={{ opacity: uploadStatus['doc6bFile'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc6bFile'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc6bFile ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc6bFile'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc6bFile'] || 0}%</span>}
                        {formData.doc6bFile && <ViewButton onClick={() => handleViewFile(formData.doc6bFile, 'Proposed Partnership Deed')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc6bFile" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc6bFile')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc6bFile && uploadStatus['doc6bFile'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc7') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>7</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Certificate of Registration for Co-operatives / Corporate Bodies</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc7File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc7File'] === 'uploading'} style={{ opacity: uploadStatus['doc7File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc7File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc7File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc7File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc7File'] || 0}%</span>}
                        {formData.doc7File && <ViewButton onClick={() => handleViewFile(formData.doc7File, 'Certificate of Registration')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc7File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc7File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc7File && uploadStatus['doc7File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc8') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>8</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Document clearly indicating Commercial Status of the offered site</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc8File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc8File'] === 'uploading'} style={{ opacity: uploadStatus['doc8File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc8File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc8File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc8File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc8File'] || 0}%</span>}
                        {formData.doc8File && <ViewButton onClick={() => handleViewFile(formData.doc8File, 'Commercial Status Document')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc8File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc8File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc8File && uploadStatus['doc8File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc9') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>9</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Land Lease Agreement between MIDC & Applicant (as applicable, Certified Copy)</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc9File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc9File'] === 'uploading'} style={{ opacity: uploadStatus['doc9File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc9File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc9File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc9File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc9File'] || 0}%</span>}
                        {formData.doc9File && <ViewButton onClick={() => handleViewFile(formData.doc9File, 'Land Lease Agreement')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc9File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc9File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc9File && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc10') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>10</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Survey Plan issued by MIDC and Plot Handover Letter with one Certified Copy <em style={{ color: '#666' }}>(Applicable for MIDC areas)</em></td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc10File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc10File'] === 'uploading'} style={{ opacity: uploadStatus['doc10File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc10File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc10File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc10File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc10File'] || 0}%</span>}
                        {formData.doc10File && <ViewButton onClick={() => handleViewFile(formData.doc10File, 'Survey Plan / Handover Letter')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc10File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc10File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc10File && uploadStatus['doc10File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc11') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>11</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>NOC from Landlord in case of Leasehold Land (Copy)</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc11File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc11File'] === 'uploading'} style={{ opacity: uploadStatus['doc11File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc11File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc11File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc11File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc11File'] || 0}%</span>}
                        {formData.doc11File && <ViewButton onClick={() => handleViewFile(formData.doc11File, 'NOC from Landlord')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc11File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc11File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc11File && uploadStatus['doc11File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc12') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>12</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>NOC from MHADA / MIDC / Municipal Corporation or applicable Local Authority (Copy)</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc12File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc12File'] === 'uploading'} style={{ opacity: uploadStatus['doc12File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc12File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc12File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc12File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc12File'] || 0}%</span>}
                        {formData.doc12File && <ViewButton onClick={() => handleViewFile(formData.doc12File, 'NOC from MHADA/MIDC')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc12File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc12File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc12File && uploadStatus['doc12File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc13') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>13</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>NA Order, as applicable (Copy)</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc13File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc13File'] === 'uploading'} style={{ opacity: uploadStatus['doc13File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc13File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc13File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc13File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc13File'] || 0}%</span>}
                        {formData.doc13File && <ViewButton onClick={() => handleViewFile(formData.doc13File, 'NA Order')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc13File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc13File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc13File && uploadStatus['doc13File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc14') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>14</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Partition / Distribution Deed <em style={{ color: '#666' }}>(in case of Family Partition / Distribution)</em> – Certified Copy</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc14File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc14File'] === 'uploading'} style={{ opacity: uploadStatus['doc14File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc14File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc14File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc14File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc14File'] || 0}%</span>}
                        {formData.doc14File && <ViewButton onClick={() => handleViewFile(formData.doc14File, 'Partition / Distribution Deed')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc14File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc14File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc14File && uploadStatus['doc14File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc15') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>15</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>Firm "Allotment Letter" issued by Govt. / Semi-Govt. bodies (if applicable, Certified Copy)</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc15File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc15File'] === 'uploading'} style={{ opacity: uploadStatus['doc15File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc15File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc15File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc15File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc15File'] || 0}%</span>}
                        {formData.doc15File && <ViewButton onClick={() => handleViewFile(formData.doc15File, 'Allotment Letter')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc15File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc15File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc15File && uploadStatus['doc15File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc16') && (
                  <tr>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: '#f8fafc' }}>16</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}><strong>If Applicant is a Registered Power of Attorney Holder:</strong><br/>Notarized Affidavit from all Owners stating that POA is not revoked / cancelled / terminated and is still valid on Rs. 500 stamp paper, along with Certified Copy of Registered POA</td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FileUploadButton onClick={() => document.getElementById('doc16File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc16File'] === 'uploading'} style={{ opacity: uploadStatus['doc16File'] === 'uploading' ? 0.7 : 1 }}>
                          {uploadStatus['doc16File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc16File ? 'Change' : 'Upload')}
                        </FileUploadButton>
                        {uploadStatus['doc16File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc16File'] || 0}%</span>}
                        {formData.doc16File && <ViewButton onClick={() => handleViewFile(formData.doc16File, 'POA Affidavit')}>View</ViewButton>}
                      </div>
                      <input type="file" id="doc16File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc16File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc16File && uploadStatus['doc16File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc17') && (
                  <tr style={{ backgroundColor: formData.panCardFile ? '#f0fdf4' : undefined }}>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: formData.panCardFile ? '#dcfce7' : '#f8fafc' }}>
                      {formData.panCardFile ? <span style={{ color: '#16a34a', fontSize: '1.2em' }}>✓</span> : '17'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>
                      Proof of Date of Birth (Copy) 
                      {formData.panCardFile ? (
                        <span style={{ color: '#16a34a', fontSize: '0.9em', fontWeight: 500, marginLeft: '8px' }}>✓ Available from Page 1 PAN Card</span>
                      ) : (
                        <em style={{ color: '#666', fontSize: '0.9em' }}>(May be auto-filled from Page 1 PAN Card)</em>
                      )}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {formData.panCardFile && !formData.doc17File ? (
                          <ViewButton onClick={() => handleViewFile(formData.panCardFile, 'PAN Card (DOB Proof)')}>View from Page 1</ViewButton>
                        ) : (
                          <>
                            <FileUploadButton onClick={() => document.getElementById('doc17File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc17File'] === 'uploading'} style={{ opacity: uploadStatus['doc17File'] === 'uploading' ? 0.7 : 1 }}>
                              {uploadStatus['doc17File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc17File ? 'Change' : 'Upload')}
                            </FileUploadButton>
                            {uploadStatus['doc17File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc17File'] || 0}%</span>}
                            {formData.doc17File && <ViewButton onClick={() => handleViewFile(formData.doc17File, 'Proof of Date of Birth')}>View</ViewButton>}
                          </>
                        )}
                      </div>
                      <input type="file" id="doc17File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc17File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc17File && uploadStatus['doc17File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                  {visibleRows.includes('doc18') && (() => {
                    const hasResidenceProof = formData.aadhaarFile || (formData.addressProofFile && formData.currentAddress);
                    const proofSource = formData.aadhaarFile ? 'Aadhaar Card' : 'Address Proof';
                    return (
                  <tr style={{ backgroundColor: hasResidenceProof ? '#f0fdf4' : undefined }}>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: hasResidenceProof ? '#dcfce7' : '#f8fafc' }}>
                      {hasResidenceProof ? <span style={{ color: '#16a34a', fontSize: '1.2em' }}>✓</span> : '18'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>
                      Proof of Residence (Copy) 
                      {hasResidenceProof ? (
                        <span style={{ color: '#16a34a', fontSize: '0.9em', fontWeight: 500, marginLeft: '8px' }}>✓ Available from Page 1 {proofSource}</span>
                      ) : (
                        <em style={{ color: '#666', fontSize: '0.9em' }}>(May be auto-filled from Page 1 Aadhaar Card)</em>
                      )}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {hasResidenceProof && !formData.doc18File ? (
                          <ViewButton onClick={() => handleViewFile(formData.aadhaarFile || formData.addressProofFile, `${proofSource} (Residence Proof)`)}>View from Page 1</ViewButton>
                        ) : (
                          <>
                            <FileUploadButton onClick={() => document.getElementById('doc18File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc18File'] === 'uploading'} style={{ opacity: uploadStatus['doc18File'] === 'uploading' ? 0.7 : 1 }}>
                              {uploadStatus['doc18File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc18File ? 'Change' : 'Upload')}
                            </FileUploadButton>
                            {uploadStatus['doc18File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc18File'] || 0}%</span>}
                            {formData.doc18File && <ViewButton onClick={() => handleViewFile(formData.doc18File, 'Proof of Residence')}>View</ViewButton>}
                          </>
                        )}
                      </div>
                      <input type="file" id="doc18File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc18File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc18File && uploadStatus['doc18File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                    );
                  })()}
                  {visibleRows.includes('doc19') && (() => {
                    const hasEducationDoc = formData.qualificationProofFile || formData.eduSscCertificateFile || formData.eduHscCertificateFile || formData.eduGraduateCertificateFile || formData.eduOthersCertificateFile;
                    const eduDocFile = formData.qualificationProofFile || formData.eduSscCertificateFile || formData.eduHscCertificateFile || formData.eduGraduateCertificateFile || formData.eduOthersCertificateFile;
                    return (
                  <tr style={{ backgroundColor: hasEducationDoc ? '#f0fdf4' : undefined }}>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: hasEducationDoc ? '#dcfce7' : '#f8fafc' }}>
                      {hasEducationDoc ? <span style={{ color: '#16a34a', fontSize: '1.2em' }}>✓</span> : '19'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>
                      Proof of Educational Qualification (Copy) 
                      {hasEducationDoc ? (
                        <span style={{ color: '#16a34a', fontSize: '0.9em', fontWeight: 500, marginLeft: '8px' }}>✓ Available from Page 1 Education docs</span>
                      ) : (
                        <em style={{ color: '#666', fontSize: '0.9em' }}>(May be auto-filled from Page 1)</em>
                      )}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {hasEducationDoc && !formData.doc19File ? (
                          <ViewButton onClick={() => handleViewFile(eduDocFile, 'Education Certificate')}>View from Page 1</ViewButton>
                        ) : (
                          <>
                            <FileUploadButton onClick={() => document.getElementById('doc19File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc19File'] === 'uploading'} style={{ opacity: uploadStatus['doc19File'] === 'uploading' ? 0.7 : 1 }}>
                              {uploadStatus['doc19File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc19File ? 'Change' : 'Upload')}
                            </FileUploadButton>
                            {uploadStatus['doc19File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc19File'] || 0}%</span>}
                            {formData.doc19File && <ViewButton onClick={() => handleViewFile(formData.doc19File, 'Proof of Educational Qualification')}>View</ViewButton>}
                          </>
                        )}
                      </div>
                      <input type="file" id="doc19File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc19File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc19File && uploadStatus['doc19File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                    );
                  })()}
                  {visibleRows.includes('doc20') && (
                  <tr style={{ backgroundColor: formData.aadhaarFile ? '#f0fdf4' : undefined }}>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center', backgroundColor: formData.aadhaarFile ? '#dcfce7' : '#f8fafc' }}>
                      {formData.aadhaarFile ? <span style={{ color: '#16a34a', fontSize: '1.2em' }}>✓</span> : '20'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9' }}>
                      Proof of Photo ID & Address 
                      {formData.aadhaarFile ? (
                        <span style={{ color: '#16a34a', fontSize: '0.9em', fontWeight: 500, marginLeft: '8px' }}>✓ Available from Page 1 Aadhaar Card</span>
                      ) : (
                        <em style={{ color: '#666', fontSize: '0.9em' }}>(Copy – Aadhaar, may be auto-filled from Page 1)</em>
                      )}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #85c1e9', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {formData.aadhaarFile && !formData.doc20File ? (
                          <ViewButton onClick={() => handleViewFile(formData.aadhaarFile, 'Aadhaar Card (Photo ID)')}>View from Page 1</ViewButton>
                        ) : (
                          <>
                            <FileUploadButton onClick={() => document.getElementById('doc20File')?.click()} disabled={!isOnline || isViewMode || isApplicationLocked || isMainApplicantSectionLocked || uploadStatus['doc20File'] === 'uploading'} style={{ opacity: uploadStatus['doc20File'] === 'uploading' ? 0.7 : 1 }}>
                              {uploadStatus['doc20File'] === 'uploading' ? (<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><UploadSpinner />Uploading...</span>) : (formData.doc20File ? 'Change' : 'Upload')}
                            </FileUploadButton>
                            {uploadStatus['doc20File'] === 'uploading' && <span style={{ fontSize: '0.8em', color: '#3498db', fontWeight: 500 }}>{uploadProgress['doc20File'] || 0}%</span>}
                            {formData.doc20File && <ViewButton onClick={() => handleViewFile(formData.doc20File, 'Proof of Photo ID & Address')}>View</ViewButton>}
                          </>
                        )}
                      </div>
                      <input type="file" id="doc20File" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleFileUpload('doc20File')} disabled={!isOnline || isViewMode || isApplicationLocked} />
                      {formData.doc20File && uploadStatus['doc20File'] !== 'uploading' && <div style={{ fontSize: '0.8em', color: '#28a745', marginTop: '4px' }}>✓ Uploaded</div>}
                    </td>
                  </tr>
                  )}
                </tbody>
              </table>
            </FormFieldFullWidth>
          </FormContentContainer>
          <PageNumber>4</PageNumber>
        </A4PageContainer>
        
      </PageWrapper>
      
      {/* Document Preview Modal with Zoom - Rendered outside PageWrapper to avoid z-index issues */}
      {isModalOpen && modalFileUrl && (
        <DocumentPreviewModal onClick={(e) => { if (e.target === e.currentTarget) { setIsModalOpen(false); setPreviewZoom(1.0); } }}>
          <DocumentPreviewHeader>
            <div style={{ fontWeight: 600, fontSize: '1.1em' }}>{modalFileName || 'Document Preview'}</div>
            <PreviewZoomControls>
              <PreviewZoomButton onClick={() => setPreviewZoom(prev => Math.max(prev - 0.2, 0.4))}>−</PreviewZoomButton>
              <span style={{ fontSize: '0.9em', minWidth: '50px', textAlign: 'center' }}>{Math.round(previewZoom * 100)}%</span>
              <PreviewZoomButton onClick={() => setPreviewZoom(prev => Math.min(prev + 0.2, 3.0))}>+</PreviewZoomButton>
              <ClosePreviewButton onClick={() => { setIsModalOpen(false); setPreviewZoom(1.0); }}>Close</ClosePreviewButton>
            </PreviewZoomControls>
          </DocumentPreviewHeader>
          <DocumentPreviewContent scale={previewZoom}>
            {modalFileType?.includes('pdf') ? (
              <object
                data={modalFileUrl}
                type="application/pdf"
                style={{ width: '100%', height: '100%', minHeight: '60vh' }}
              >
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <p style={{ marginBottom: '20px', color: '#666' }}>Unable to display PDF in browser.</p>
                  <a 
                    href={modalFileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      background: '#1a5276', 
                      color: 'white', 
                      padding: '12px 24px', 
                      borderRadius: '6px', 
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}
                  >
                    Open PDF in New Tab
                  </a>
                </div>
              </object>
            ) : (
              <img 
                src={modalFileUrl} 
                alt={modalFileName || 'Document Preview'}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            )}
          </DocumentPreviewContent>
          {modalFileType?.includes('pdf') && (
            <div style={{ marginTop: '10px' }}>
              <a 
                href={modalFileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: 'white', 
                  textDecoration: 'underline',
                  fontSize: '0.9em'
                }}
              >
                Open in New Tab ↗
              </a>
            </div>
          )}
        </DocumentPreviewModal>
      )}
      
      {/* Empty ActionButtonsContainer to maintain structure */}
      <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000 }}>
        {/* Next Page button moved near FormPageNavigator */}
      </ActionButtonsContainer>
      
      {/* Session Timer */}
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Application ID Display */}
      {applicationId && <ApplicationIdDisplay applicationId={applicationId} />}

      {/* Home Icon Button - Above Save Icon */}
      <button
        onClick={() => {
          // Skip save modal if application is locked
          if (applicationId && isOnline && !isApplicationLocked) {
            setPendingNavigation('/user-dashboard');
            setShowSaveModal(true);
          } else {
            navigate('/user-dashboard');
          }
        }}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '20px',
          bottom: '150px',
          zIndex: 1000,
          height: '44px',
          padding: '0 16px 0 12px',
          borderRadius: '22px',
          border: 'none',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
          transition: 'all 0.25s ease'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        Home
      </button>

      {isPdfGenerating && (
        <ModalOverlay>
          <ModalContent>
            <h3>Generating PDF</h3>
            <p>Please wait... Generating PDF</p>
            <ProgressBarContainerModal>
              <ProgressBarFillModal progress={compressionProgress} />
            </ProgressBarContainerModal>
            <ProgressText>Compression: {compressionProgress}%</ProgressText>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Save Icon Button - Above Back Button - Hidden when application is locked */}
      {!isViewMode && !isApplicationLocked && (
        <button
          onClick={() => saveToDatabaseCallback(formData, true, applicationId)}
          disabled={isSaving || !applicationId || !isOnline}
          title="Save Draft"
          style={{
            position: 'fixed',
            left: '20px',
            bottom: '98px',
            zIndex: 1000,
            height: '44px',
            padding: '0 16px 0 12px',
            borderRadius: '22px',
            border: 'none',
            background: isSaving ? '#86efac' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            cursor: isSaving || !applicationId || !isOnline ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)',
            transition: 'all 0.25s ease',
            opacity: isSaving || !applicationId || !isOnline ? 0.6 : 1
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/>
            <polyline points="7 3 7 8 15 8"/>
          </svg>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      )}

      {/* Save Progress Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2200,
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '28px 32px',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            maxWidth: '380px',
            width: '90%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>💾</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50', fontSize: '1.15em', fontWeight: 600 }}>
              Save your progress?
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.9em', color: '#666', lineHeight: 1.5 }}>
              Would you like to save your form before continuing?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleSkipSaveAndNavigate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f1f1',
                  color: '#555',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleSaveAndNavigate}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brochure PDF Modal */}
      {showBrochureModal && (
        <div 
          onClick={() => setShowBrochureModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90vw',
              maxWidth: '900px',
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f8fafc'
            }}>
              <h3 style={{ margin: 0, color: '#1e40af', fontWeight: 600 }}>📄 MNGL DODO Brochure</h3>
              <button
                onClick={() => setShowBrochureModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                ✕
              </button>
            </div>
            <iframe
              src="/brochures/mngl-dodo-brochure.pdf"
              style={{
                flex: 1,
                width: '100%',
                border: 'none'
              }}
              title="MNGL DODO Brochure"
            />
          </div>
        </div>
      )}

      {/* Upload Error Modal */}
      {uploadErrorModal.show && (
        <UploadErrorModal onClick={() => setUploadErrorModal({ show: false, message: '', fieldName: '' })}>
          <UploadErrorContent onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ color: '#e74c3c', marginBottom: '12px' }}>Document Upload Failed</h3>
            <p style={{ color: '#666', marginBottom: '8px', fontWeight: 500 }}>
              Field: {uploadErrorModal.fieldName}
            </p>
            <p style={{ color: '#333', marginBottom: '20px', lineHeight: '1.5' }}>
              {uploadErrorModal.message}
            </p>
            <button
              onClick={() => setUploadErrorModal({ show: false, message: '', fieldName: '' })}
              style={{
                padding: '10px 24px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.95em',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK, I'll Upload the Correct Document
            </button>
          </UploadErrorContent>
        </UploadErrorModal>
      )}
    </>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const ProgressBarContainerModal = styled.div`
  width: 100%;
  height: 20px;
  background-color: #e9ecef;
  border-radius: 10px;
  margin-top: 20px;
  overflow: hidden;
`;

const ProgressBarFillModal = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%; 
  background-color: #6cb288;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled.p`
  margin-top: 10px;
  font-size: 0.9em;
  color: #555;
`;

export default ApplicationFormPage4;